import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { OSVersionDetailsSlideout } from '../components/OSVersionDetailsSlideout';
import { EOLStatusPanel } from '../components/EOLStatusPanel';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

interface OSProduct {
  id: number;
  name: string;
  manufacturer: string;
  family_name: string;
  family_slug: string;
  cpe_base: string;
  os_variant: string;
  major_version: number;
  version_pattern: string;
  release_date: string;
  end_of_support: string;
  is_supported: boolean;
  vendor_url: string;
  documentation_url: string;
}

interface OSVersion {
  id: number;
  version_number: string;
  display_version: string;
  build_number: string;
  kernel_version: string;
  architecture: string;
  feature_update: string;
  update_classification: string;
  is_security_update: boolean;
  vendor_advisory_id: string;
  vendor_advisory_url: string;
  release_date: string;
  release_channel: string;
  is_latest: boolean;
  cve_count?: number;
  vulnerable_count?: number;
  patched_count?: number;
  eol_status?: string;
  days_until_eol?: number;
  eol_risk_level?: string;
  eol_cycle?: string;
  eol_date?: string;
}

export function OSProductDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<OSProduct | null>(null);
  const [versions, setVersions] = useState<OSVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedArchitecture, setSelectedArchitecture] = useState<string>('all');
  const [selectedFeature, setSelectedFeature] = useState<string>('all');
  const [selectedVersionId, setSelectedVersionId] = useState<number | null>(null);
  const [sortField, setSortField] = useState<string>('release_date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);

  useEffect(() => {
    if (id) {
      loadProductDetails();
      loadVersions();
    }
  }, [id, sortField, sortDirection]);

  const loadProductDetails = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/os/products/${id}`);
      if (!response.ok) throw new Error('Failed to fetch');
      const data = await response.json();
      setProduct(data);
    } catch (err) {
      setError('Failed to load OS product details');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadVersions = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/os/versions?product_id=${id}&limit=100&sort_by=${sortField}&sort_dir=${sortDirection}`);
      if (!response.ok) throw new Error('Failed to fetch');
      const data = await response.json();
      setVersions(data);
    } catch (err) {
      console.error('Failed to load versions:', err);
    }
  };

  // Filter by architecture and feature update (client-side filtering on server-sorted results)
  const filteredVersions = versions.filter((v) => {
    if (selectedArchitecture !== 'all' && v.architecture !== selectedArchitecture) {
      return false;
    }
    if (selectedFeature !== 'all' && v.feature_update !== selectedFeature) {
      return false;
    }
    return true;
  });

  // Pagination
  const totalPages = Math.ceil(filteredVersions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedVersions = filteredVersions.slice(startIndex, endIndex);

  const architectures = ['all', ...new Set(versions.map((v) => v.architecture))];
  const featureUpdates = ['all', ...new Set(versions.map((v) => v.feature_update).filter(Boolean))].sort();

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) {
      return <span className="ml-1 text-gray-400">↕</span>;
    }
    return <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading OS product...</p>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Product not found'}</p>
          <button
            onClick={() => navigate('/os')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to OS List
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/os')}
            className="mb-6 text-blue-600 hover:text-blue-700 flex items-center font-medium transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Operating Systems
          </button>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-3xl font-bold text-gray-900">{product.name}</h1>
                  <span
                    className={`
                      px-3 py-1 rounded-full text-xs font-semibold
                      ${product.is_supported
                        ? 'bg-green-100 text-green-700 border border-green-200'
                        : 'bg-red-100 text-red-700 border border-red-200'
                      }
                    `}
                  >
                    {product.is_supported ? 'Supported' : 'End of Life'}
                  </span>
                </div>
                <p className="text-gray-600 text-lg">{product.manufacturer} • {product.family_name}</p>
              </div>
            </div>

            {/* Quick Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-gray-200">
              <div>
                <p className="text-sm text-gray-500 mb-1">Major Version</p>
                <p className="text-xl font-semibold text-gray-900">{product.major_version || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Variant</p>
                <p className="text-xl font-semibold text-gray-900 capitalize">{product.os_variant || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Total Versions</p>
                <p className="text-xl font-semibold text-gray-900">{versions.length}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Architectures</p>
                <p className="text-xl font-semibold text-gray-900">{architectures.length - 1}</p>
              </div>
            </div>

            {/* Links */}
            <div className="flex gap-4 mt-6 pt-6 border-t border-gray-200">
              {product.vendor_url && (
                <a
                  href={product.vendor_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium transition-colors"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                  Vendor Website
                </a>
              )}
              {product.documentation_url && (
                <a
                  href={product.documentation_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium transition-colors"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                  Documentation
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Product Info - Smaller */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Technical Details</h2>
            <dl className="space-y-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">Version Pattern</dt>
                <dd className="mt-1 text-sm text-gray-900 font-mono">{product.version_pattern || 'N/A'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">CPE Base</dt>
                <dd className="mt-1 text-xs text-gray-900 font-mono break-all bg-gray-50 p-2 rounded">
                  {product.cpe_base}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">End of Support</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {product.end_of_support ? new Date(product.end_of_support).toLocaleDateString() : 'Not specified'}
                </dd>
              </div>
            </dl>
          </div>

          {/* EOL Status - Larger */}
          <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <EOLStatusPanel productId={parseInt(id!)} productType="os" />
          </div>
        </div>

        {/* Versions Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-5 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Version History</h2>
                <p className="mt-1 text-sm text-gray-500">{filteredVersions.length} versions found</p>
              </div>
              <div className="flex items-center gap-3">
                {featureUpdates.length > 1 && (
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700">Feature:</label>
                    <select
                      value={selectedFeature}
                      onChange={(e) => {
                        setSelectedFeature(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {featureUpdates.map((feature) => (
                        <option key={feature} value={feature}>
                          {feature === 'all' ? 'All Features' : feature}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700">Architecture:</label>
                  <select
                    value={selectedArchitecture}
                    onChange={(e) => {
                      setSelectedArchitecture(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {architectures.map((arch) => (
                      <option key={arch} value={arch}>
                        {arch === 'all' ? 'All Architectures' : arch}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {filteredVersions.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('display_version')}
                    >
                      <div className="flex items-center">
                        Version <SortIcon field="display_version" />
                      </div>
                    </th>
                    {featureUpdates.length > 1 && (
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('feature_update')}
                      >
                        <div className="flex items-center">
                          Feature <SortIcon field="feature_update" />
                        </div>
                      </th>
                    )}
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('architecture')}
                    >
                      <div className="flex items-center">
                        Architecture <SortIcon field="architecture" />
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Build
                    </th>
                    <th
                      className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('vulnerable_count')}
                    >
                      <div className="flex items-center justify-center">
                        CVEs <SortIcon field="vulnerable_count" />
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('release_date')}
                    >
                      <div className="flex items-center">
                        Released <SortIcon field="release_date" />
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedVersions.map((version) => (
                    <tr
                      key={version.id}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => setSelectedVersionId(version.id)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-gray-900">
                          {version.display_version || version.version_number}
                        </div>
                      </td>
                      {featureUpdates.length > 1 && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-700">
                            {version.feature_update || '-'}
                          </div>
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {version.architecture}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-600 font-mono">
                          {version.build_number || version.kernel_version || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center gap-3">
                          <div className="flex flex-col items-center">
                            <span className={`text-sm font-semibold ${
                              version.vulnerable_count && version.vulnerable_count > 0
                                ? 'text-red-600'
                                : 'text-gray-400'
                            }`}>
                              {version.vulnerable_count || 0}
                            </span>
                            <span className="text-xs text-gray-500">affected</span>
                          </div>
                          <div className="flex flex-col items-center">
                            <span className={`text-sm font-semibold ${
                              version.patched_count && version.patched_count > 0
                                ? 'text-green-600'
                                : 'text-gray-400'
                            }`}>
                              {version.patched_count || 0}
                            </span>
                            <span className="text-xs text-gray-500">patched</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-700">
                          {version.release_date
                            ? new Date(version.release_date).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })
                            : 'Unknown'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex gap-2">
                          {version.is_latest && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                              Latest
                            </span>
                          )}
                          {version.is_security_update && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-700">
                              Security
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
                  <div className="text-sm text-gray-700">
                    Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                    <span className="font-medium">{Math.min(endIndex, filteredVersions.length)}</span> of{' '}
                    <span className="font-medium">{filteredVersions.length}</span> results
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                        currentPage === 1
                          ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      Previous
                    </button>

                    <div className="flex gap-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }

                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                              currentPage === pageNum
                                ? 'bg-blue-600 text-white border-blue-600'
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>

                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                        currentPage === totalPages
                          ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No versions found</h3>
              <p className="mt-1 text-sm text-gray-500">
                No versions match your current filters.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Version Details Slideout */}
      <OSVersionDetailsSlideout
        versionId={selectedVersionId}
        onClose={() => setSelectedVersionId(null)}
      />
    </div>
  );
}
